import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "next-themes";
import { clinicConfig } from "@/config/clinic";

const NAV = [
  { label: "Care Options", href: "#care" },
  { label: "The Doctor", href: "#doctor" },
  { label: "Visit Us", href: "#visit" },
  { label: "FAQ", href: "#faq" },
];

function ThemeToggle({ overHero }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && theme === "dark";
  return (
    <button
      data-testid="theme-toggle"
      aria-label="Toggle dark mode"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className={`grid h-9 w-9 place-items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
        overHero
          ? "border-white/30 text-white/90 hover:bg-white/10"
          : "border-border text-foreground/70 hover:bg-secondary hover:text-foreground"
      }`}
    >
      {isDark ? <Sun className="h-4 w-4" strokeWidth={1.5} /> : <Moon className="h-4 w-4" strokeWidth={1.5} />}
    </button>
  );
}

export const Header = ({ onRequest }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const overHero = !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        overHero
          ? "border-b border-transparent bg-transparent"
          : "border-b border-border bg-background/85 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16" : "h-20"}`}>
          <a href="#top" data-testid="header-logo" className="flex items-center gap-3 focus:outline-none">
            <img
              src={overHero ? "/logo-cream.png" : "/logo-teal.png"}
              alt=""
              className="h-9 w-9 object-contain dark:hidden"
            />
            <img src="/logo-cream.png" alt="" className="hidden h-9 w-9 object-contain dark:block" />
            <span className="hidden leading-tight sm:block">
              <span className={`block font-serif text-lg font-semibold tracking-tight ${overHero ? "text-white" : "text-foreground"}`}>
                West Virginia
              </span>
              <span className={`block text-[0.65rem] font-bold uppercase tracking-[0.22em] ${overHero ? "text-white/70" : "text-muted-foreground"}`}>
                Family Medicine
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-8 lg:flex">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.href.slice(1)}`}
                className={`text-sm font-medium transition-colors ${
                  overHero ? "text-white/80 hover:text-white" : "text-foreground/70 hover:text-foreground"
                }`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={`tel:${clinicConfig.phoneTel}`}
              data-testid="header-phone"
              className={`hidden items-center gap-2 text-sm font-medium transition-colors md:flex ${
                overHero ? "text-white/85 hover:text-white" : "text-foreground/80 hover:text-foreground"
              }`}
            >
              <Phone className="h-4 w-4" strokeWidth={1.5} />
              {clinicConfig.phoneDisplay}
            </a>
            <ThemeToggle overHero={overHero} />
            <a
              href={clinicConfig.patientPortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-signin-cta"
              className={`hidden rounded-full px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring sm:inline-block ${
                overHero
                  ? "bg-accent text-accent-foreground hover:bg-accent/90"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
            >
              Patient Sign In
            </a>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              onClick={() => setMenuOpen((v) => !v)}
              className={`grid h-9 w-9 place-items-center rounded-full border lg:hidden ${
                overHero ? "border-white/30 text-white" : "border-border text-foreground"
              }`}
            >
              {menuOpen ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-border bg-background lg:hidden"
          >
            <div className="mx-auto max-w-7xl space-y-1 px-6 py-4">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setMenuOpen(false)}
                  className="block rounded-md px-3 py-3 text-base font-medium text-foreground/80 hover:bg-secondary"
                >
                  {n.label}
                </a>
              ))}
              <a
                href={`tel:${clinicConfig.phoneTel}`}
                className="flex items-center gap-2 rounded-md px-3 py-3 text-base font-medium text-foreground/80 hover:bg-secondary"
              >
                <Phone className="h-4 w-4" strokeWidth={1.5} /> {clinicConfig.phoneDisplay}
              </a>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onRequest();
                }}
                className="mt-2 w-full rounded-full border border-primary px-5 py-3 text-sm font-semibold text-primary"
              >
                Book or Request Care
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
